import type { FC, FormEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'loading' | 'success' | 'error'

type SubscribeResponse = {
    message?: string
}

export const NewsletterForm: FC = (): ReactNode => {
    const inputId = useId()
    const messageId = useId()
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const form = event.currentTarget
        const honeypot = form.elements.namedItem('company')
        const company = honeypot instanceof HTMLInputElement ? honeypot.value : ''

        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, company })
            })

            const data = await response.json().catch(() => null) as SubscribeResponse | null

            if (!response.ok) {
                setStatus('error')
                setMessage(data?.message ?? 'Something went wrong. Please try again.')
                return
            }

            setStatus('success')
            setMessage(data?.message ?? "You're on the list. Thanks for subscribing.")
            setEmail('')
        }
        catch {
            setStatus('error')
            setMessage('We could not reach the server. Please check your connection and try again.')
        }
    }

    if (status === 'success') {
        return (
            <div
                role='status'
                className='flex items-start gap-3 rounded-xl border border-primary-foreground/15 bg-primary-foreground/[0.06] p-5 animate-in fade-in duration-500'
            >
                <CircleCheck className='mt-0.5 size-5 shrink-0 text-brand-soft' aria-hidden='true' />

                <div>
                    <p className='font-medium text-primary-foreground'>
                        {message}
                    </p>

                    <p className='mt-1 text-sm text-primary-foreground/70'>
                        Keep an eye on your inbox for the next new story.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='w-full'>
            <label htmlFor={inputId} className='text-sm font-medium text-primary-foreground/80'>
                Email address
            </label>

            <div className='mt-2 flex flex-col gap-3 sm:flex-row'>
                <input
                    id={inputId}
                    type='email'
                    name='email'
                    required
                    autoComplete='email'
                    inputMode='email'
                    placeholder='you@example.com'
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    aria-invalid={status === 'error'}
                    aria-describedby={message ? messageId : undefined}
                    className='h-12 w-full min-w-0 flex-1 rounded-full border border-primary-foreground/20 bg-primary-foreground/[0.07] px-5 text-[0.95rem] text-primary-foreground placeholder:text-primary-foreground/45 transition-colors focus:border-primary-foreground/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/70'
                />

                <button
                    type='submit'
                    disabled={status === 'loading'}
                    className='inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-brand px-6 text-sm font-semibold text-brand-foreground transition-colors hover:bg-brand/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground disabled:cursor-not-allowed disabled:opacity-70'
                >
                    {status === 'loading'
                        ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        : <ArrowRight className='size-4' aria-hidden='true' />}
                    {status === 'loading' ? 'Subscribing' : 'Subscribe'}
                </button>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label>
                    Company
                    <input type='text' name='company' tabIndex={-1} autoComplete='off' defaultValue='' />
                </label>
            </div>

            <p
                id={messageId}
                aria-live='polite'
                className={cn('mt-3 min-h-5 text-sm', status === 'error' ? 'text-brand-soft' : 'text-primary-foreground/60')}
            >
                {status === 'error' ? message : 'One short email when something new is published. No spam, ever.'}
            </p>
        </form>
    )
}
